import { supabase } from './supabase';

export interface HorizontalNavConfig {
  id?: string;
  business_id: string;
  resource_id?: string | null;
  user_id?: string | null;
  slot_0?: string | null;
  slot_1?: string | null;
  slot_2?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface NavItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  pageId?: string;
  permissionKey?: string;
}

/**
 * Load horizontal navigation configuration for the current user
 */
export async function loadHorizontalNavConfig(
  businessId: string,
  userId: string,
  resourceId?: string
): Promise<(NavItem | null)[]> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized');
      return [null, null, null];
    }

    let query = supabase
      .from('horizontal_nav_config')
      .select('*')
      .eq('business_id', businessId);

    // For resource accounts, filter by resource_id
    // For admin accounts, filter by user_id
    if (resourceId) {
      query = query.eq('resource_id', resourceId);
    } else {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error loading horizontal nav config:', error);
      return [null, null, null];
    }

    if (!data) {
      // No configuration found, return empty slots
      return [null, null, null];
    }

    // Convert slot data back to NavItem objects
    // Note: We'll need to match against our available items
    const slots: (NavItem | null)[] = [
      data.slot_0 ? { id: data.slot_0 } as NavItem : null,
      data.slot_1 ? { id: data.slot_1 } as NavItem : null,
      data.slot_2 ? { id: data.slot_2 } as NavItem : null,
    ];

    return slots;
  } catch (error) {
    console.error('Error in loadHorizontalNavConfig:', error);
    return [null, null, null];
  }
}

/**
 * Save horizontal navigation configuration for the current user
 */
export async function saveHorizontalNavConfig(
  businessId: string,
  userId: string,
  slots: (NavItem | null)[],
  resourceId?: string
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized');
      return false;
    }

    const configData: Partial<HorizontalNavConfig> = {
      business_id: businessId,
      slot_0: slots[0]?.id || null,
      slot_1: slots[1]?.id || null,
      slot_2: slots[2]?.id || null,
    };

    // Set either resource_id or user_id based on account type
    if (resourceId) {
      configData.resource_id = resourceId;
      configData.user_id = null;
    } else {
      configData.user_id = userId;
      configData.resource_id = null;
    }

    // Use upsert to insert or update existing configuration
    const { error } = await supabase
      .from('horizontal_nav_config')
      .upsert(configData, {
        onConflict: resourceId ? 'business_id,resource_id' : 'business_id,user_id'
      });

    if (error) {
      console.error('Error saving horizontal nav config:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in saveHorizontalNavConfig:', error);
    return false;
  }
}

/**
 * Clear horizontal navigation configuration for the current user
 */
export async function clearHorizontalNavConfig(
  businessId: string,
  userId: string,
  resourceId?: string
): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized');
      return false;
    }

    let query = supabase
      .from('horizontal_nav_config')
      .delete()
      .eq('business_id', businessId);

    if (resourceId) {
      query = query.eq('resource_id', resourceId);
    } else {
      query = query.eq('user_id', userId);
    }

    const { error } = await query;

    if (error) {
      console.error('Error clearing horizontal nav config:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in clearHorizontalNavConfig:', error);
    return false;
  }
}

/**
 * Get all horizontal navigation configurations for a business (admin only)
 */
export async function getAllHorizontalNavConfigs(
  businessId: string
): Promise<HorizontalNavConfig[]> {
  try {
    if (!supabase) {
      console.error('Supabase client not initialized');
      return [];
    }

    const { data, error } = await supabase
      .from('horizontal_nav_config')
      .select('*')
      .eq('business_id', businessId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error getting all horizontal nav configs:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllHorizontalNavConfigs:', error);
    return [];
  }
} 